import { fromJS } from 'immutable';
import * as constants from './partnerFinder-constants.js';
import { pathOr, propOr, path, equals, pluck, contains, merge } from 'ramda';

const initialState = fromJS({
  results: undefined,
  searchParams: {},
  page: 1,
  nextPage: undefined,
  status: undefined,
  total: 0,
  prevPayload: {},
  threeConnectedCustomers: [],
  connectionsWithMM: [], // user clicked on MM [{ companyId, media: [urls] }]
  sendCustomMsg: {
    open: false,
    popOverAnchorEl: undefined,
    customizedMessage: '',
    receiverInfo: {},
  },
  responseTime: [],
});

export function partnerFinder(state = initialState, action) {
  const connectionId = propOr('', 'connectionId', action);
  switch (action.type) {
    case constants.OPEN_PF_CUSTOM_MSG_POPOVER:
      return state.setIn(['sendCustomMsg', 'open'], true).update('sendCustomMsg', sendCustomMsg => sendCustomMsg.merge(propOr({}, 'payload', action)));
    case constants.CLOSE_PF_CUSTOM_MSG_POPOVER:
      return state.update('sendCustomMsg', sendCustomMsg => sendCustomMsg.merge({
        open: false,
        popOverAnchorEl: undefined,
        customizedMessage: '',
      }));
    case constants.UPDATE_CUSTOM_MSG:
      return state.setIn(['sendCustomMsg', 'customizedMessage'], pathOr('', ['payload', 'customizedMessage'], action));
    case 'MODIFY_PARAMS':
      return state.set('searchParams', fromJS(action.params));
    case 'SEARCH_STARTING':
      return state.set('status', 'isSyncing')
                  .set('threeConnectedCustomers', '');
    case constants.SEARCH_COUNT_ONLY:
      return state.set('total', action.payload.total);
    case 'SEARCH':
      if (Array.isArray(action.payload) || typeof action.payload === 'string') {
        return state.set('results', action.payload)
                    .set('total', Array.isArray(action.payload) ? action.payload.length : action.payload)
                    .set('status', 'success');
      }
      if (typeof action.payload === 'object') {
        return state.merge(fromJS(action.payload))
          .set('status', 'success')
          .set('threeConnectedCustomers', fromJS(action.payload.threeConnectedCustomers))
          .set('fromPFPayload', state.get('prevPayload')) // for returning to PF search results from profile
          .set('prevPFSearch', state.get('searchParams')) // for returning to PF search results from profile
          .set('prevPayload', fromJS(action.payload));
      }
      break;
    case 'SEARCH_FAILED':
      return state.set('results', [])
                  .set('status', 'failed');
    case 'LOAD_MORE':
      if (state.get('page') === state.get('nextPage')) return state;
      if (state.getIn(['prevPayload', 'prevPage']) === action.payload.prevPage && state.getIn(['prevPayload', 'nextPage']) === action.payload.nextPage) return state;
      return state.mergeWith((prev, next, key) => {
        if (key === 'results') return prev.concat(next);
        return next;
      }, fromJS(action.payload)).set('status', 'success')
      .set('page', state.get('nextPage'))
      .set('prevPayload', fromJS(action.payload));
    case 'SEND_INVITATION_PF':
      if (!state.get('results')) return state;
      const compId = propOr('', 'companyId', action);
      return state.update('results', companies => companies.map(company => {
        const currentCompanyId = fromJS(company).get('companyId') || fromJS(company).get('supplierId');
        if (equals(compId, currentCompanyId)) {
          return fromJS(company).merge({
            status: 'pending',
            lastSent: 'a few seconds ago',
            isSender: true,
            loader: false,
          });
        }
        return company;
      }));
    case 'RESEND_INVITATION_PF':
    case 'FAKE_RESEND':
      if (!state.get('results')) return state;
      return state.update('results', companies => companies.map(company => {
        const currentConnectionId = fromJS(company).get('connectionId');
        if (equals(connectionId, currentConnectionId)) {
          return fromJS(company).merge({
            lastSent: 'a few seconds ago',
            loader: false,
          });
        }
        return company;
      }));
    case 'IGNORE_CONNECTION_PF':
      if (!state.get('results')) return state;
      return state.update('results', companies => companies.map(company => {
        const currentConnectionId = fromJS(company).get('connectionId');
        if (equals(connectionId, currentConnectionId)) {
          return fromJS(company).merge({
            status: 'rejected',
            isSender: false,
            loader: false,
          });
        }
        return company;
      }));
    case 'ACCEPT_CONNECTION_PF':
      if (!state.get('results')) return state;
      return state.update('results', companies => companies.map(company => {
        const currentConnectionId = fromJS(company).get('connectionId');
        if (equals(connectionId, currentConnectionId)) {
          return fromJS(company).merge({
            status: 'accepted',
            isSender: false,
            loader: false,
          });
        }
        return company;
      }));
    case 'FLIP_LOADER_STATUS':
      if (!state.get('results')) return state;
      const connId = path(['payload', 'connectionId'], action);
      const companyId = path(['payload', 'companyId'], action);
      return state.update('results', companies => companies.map(company => {
        const currentConnectionId = fromJS(company).get('connectionId');
        const currentCompanyId = fromJS(company).get('companyId') || fromJS(company).get('supplierId');
        if ((connId && equals(connId, currentConnectionId)) ||
          (companyId && equals(company, currentCompanyId))) return fromJS(company).set('loader', !fromJS(company).get('loader'));
        return company;
      }));
    case 'GOT_RESPONSE_TIME':
      return state.update('responseTime', responseTime => responseTime.push({
        companyId: action.payload.companyId,
        daysToRespond: action.payload.response,
      }));
    // ADDS MM ON AN ONCLICK FUNC
    // case constants.GOT_MARKETING_MATERIALS:
    //   const connectionsWithMMJS = state.get('connectionsWithMM').toJS();
    //   const isIdInConnectionsWithMM = contains(action.payload.companyId, connectionsWithMMJS.map(item => item.companyId));
    //   if (isIdInConnectionsWithMM) {
    //     return state.update('connectionsWithMM', connectionsWithMM => connectionsWithMM.filter(item => item.companyId !== action.payload.companyId));
    //   }
    //   const filterForImages = action.payload.locationId ?
    //     (action.payload.media || []).filter(file => contains(action.payload.locationId, file.tags) && (file.mediaType === 'image/jpeg' || file.mediaType === 'image/png'))
    //     : (action.payload.media || []).filter(file => file.mediaType === 'image/jpeg' || file.mediaType === 'image/png');
    //   const MMObj = {
    //     companyId: action.payload.companyId,
    //     media: pluck('url')(filterForImages),
    //   };
    //   return state.update('connectionsWithMM', connectionsWithMM => connectionsWithMM.push(
    //     action.payload.locationId ? merge(MMObj, { locationId: action.payload.locationId }) : MMObj));
    case 'CLEAR_STORES':
      return initialState;
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/app/stores/partner-finder/partnerFinder-reducer.js